/**
 * UI Event Handlers Module
 */
import Konva from "konva";
import { CF7_FORM_ID } from "./constants.js";

export class EventHandlers {
	constructor(
		stageManager,
		textManager,
		photoEditor,
		decorManager,
		monumentManager,
		appState,
	) {
		this.stageManager = stageManager;
		this.textManager = textManager;
		this.photoEditor = photoEditor;
		this.decorManager = decorManager;
		this.monumentManager = monumentManager;
		this.appState = appState;

		this.$accordion = jQuery(".mc-accordion");
		this.selectionRect = null;
		this.selectionDragOrigin = null;
		this.selectionDragActive = false;
		this.$layersPanel = null;
		this.$layersList = null;
		this.$layersTrigger = null;
		this.layersNodeIndex = new Map();
		this.isLayersPanelOpen = false;
	}

	/**
	 * Initialize all event handlers
	 */
	init() {
		this.initAccordionHandlers();
		this.initTextHandlers();
		this.initPhotoHandlers();
		this.initDecorHandlers();
		this.initCandleHandlers();
		this.initMonumentHandlers();
		this.initElementToolbarHandlers();
		this.initLayersPanel();
		this.initStageHandlers();
		this.initKeyboardHandlers();
		this.initFormHandlers();
	}

	/**
	 * Position toolbar above active element
	 */
	positionToolbar(target, $toolbar) {
		if (!$toolbar.length || !target) return;

		const stage = this.stageManager.getStage();
		let rect = null;
		if (typeof target.getClientRect === "function") {
			rect = target.getClientRect({ relativeTo: stage });
		} else if (
			target &&
			typeof target.x === "number" &&
			typeof target.y === "number" &&
			typeof target.width === "number" &&
			typeof target.height === "number"
		) {
			rect = target;
		}
		if (!rect) return;

		const padding = 6;
		const toolbarWidth = $toolbar.outerWidth();
		const toolbarHeight = $toolbar.outerHeight();
		const stageWidth = stage.width();
		const stageHeight = stage.height();

		// Increased offset to 40 to avoid covering the rotation anchor
		let top = rect.y - toolbarHeight - 30;
		let left = rect.x + rect.width / 2 - toolbarWidth / 2;

		if (top < padding) {
			top = rect.y + rect.height + padding;
		}

		if (top + toolbarHeight > stageHeight - padding) {
			top = Math.max(padding, stageHeight - toolbarHeight - padding);
		}

		const maxLeft = stageWidth - toolbarWidth - padding;
		left = Math.min(Math.max(left, padding), maxLeft);

		$toolbar.css({
			top: `${top}px`,
			left: `${left}px`,
			right: "auto",
		});
	}

	/**
	 * Accordion navigation handlers
	 */
	initAccordionHandlers() {
		this.$accordion.on("click", ".mc-acc-header", function () {
			const $item = jQuery(this).closest(".mc-acc-item");
			const isActive = $item.hasClass("mc-acc-active");

			// Don't close if clicking on already active item (keep it open)
			if (isActive) return;

			// Close all other items
			jQuery(".mc-acc-item").removeClass("mc-acc-active");

			// Open clicked item
			$item.addClass("mc-acc-active");
		});
		this.$accordion.on("click", ".mc-next", function () {
			const $item = jQuery(this).closest(".mc-acc-item");
			const step = parseInt($item.data("step"), 10);
			jQuery('.mc-acc-item[data-step="' + (step + 1) + '"]').addClass(
				"mc-acc-active",
			);
			jQuery(".mc-acc-item")
				.not('[data-step="' + (step + 1) + '"]')
				.removeClass("mc-acc-active");
		});

		this.$accordion.on("click", ".mc-prev", function () {
			const $item = jQuery(this).closest(".mc-acc-item");
			const step = parseInt($item.data("step"), 10);
			jQuery('.mc-acc-item[data-step="' + (step - 1) + '"]').addClass(
				"mc-acc-active",
			);
			jQuery(".mc-acc-item")
				.not('[data-step="' + (step - 1) + '"]')
				.removeClass("mc-acc-active");
		});
	}

	/**
	 * Text-related event handlers
	 */
	initTextHandlers() {
		// Add new text
		jQuery(".mc-add-text").on("click", () => {
			const stage = this.stageManager.getStage();
			const cx = stage.width() / 2;
			const cy = stage.height() / 2;
			const text = this.textManager.createTextNode(
				window.MC_DATA.i18n.text,
				cx,
				cy,
			);
			this.textManager.setActiveText(text);
			this.textManager.openTextConfig();
		});

		// Close text config
		jQuery(".mc-text-config-close, .mc-text-ok").on("click", () => {
			this.textManager.closeTextConfig();
			this.textManager.setActiveText(null);
		});

		// Update text content
		jQuery(".mc-text-input").on("input", (e) => {
			this.textManager.updateTextContent(jQuery(e.target).val());
		});

		// Update font family
		jQuery(".mc-font-family").on("change", (e) => {
			this.textManager.updateFontFamily(jQuery(e.target).val());
		});

		// Update font size
		jQuery(".mc-font-size-range").on("input", (e) => {
			const size = parseInt(jQuery(e.target).val(), 10) || 24;
			this.textManager.updateFontSize(size);
		});

		// Update alignment
		jQuery(".mc-align-btn").on("click", (e) => {
			const $btn = jQuery(e.currentTarget);
			const align = $btn.data("align");
			if (!align) return;

			this.textManager.updateAlignment(align);
			jQuery(".mc-align-btn").removeClass("mc-align-active");
			$btn.addClass("mc-align-active");
		});

		// Update text color
		jQuery(".mc-text-color-options").on(
			"click",
			".mc-text-color-option",
			(e) => {
				e.preventDefault();
				this.textManager.selectColorOption(jQuery(e.currentTarget));
			},
		);

		// Duplicate text
		jQuery(".mc-duplicate-text").on("click", () => {
			this.textManager.duplicateActiveText();
		});

		// Delete text
		jQuery(".mc-text-delete").on("click", () => {
			this.textManager.deleteActiveText();
			this.textManager.closeTextConfig();
		});
	}

	/**
	 * Photo-related event handlers
	 */
	initPhotoHandlers() {
		const $photoInput = jQuery(".mc-photo-input");

		// Add photo button
		jQuery(".mc-add-photo").on("click", () => {
			$photoInput.trigger("click");
		});

		// Photo file input change
		$photoInput.on("change", (e) => {
			const file = e.target.files && e.target.files[0];
			if (!file) return;

			const reader = new FileReader();
			reader.onload = (ev) => {
				const src = ev.target.result;
				this.photoEditor.openPhotoEditor(src);
			};
			reader.readAsDataURL(file);

			jQuery(e.target).val("");
		});

		// Photo zoom
		jQuery(".mc-photo-zoom").on("input", (e) => {
			const factor = parseFloat(jQuery(e.target).val()) || 1;
			this.photoEditor.zoomPhoto(factor);
		});

		// Photo shape toggle
		jQuery(".mc-photo-shape-btn").on("click", (e) => {
			const $btn = jQuery(e.currentTarget);
			const shape = ($btn.data("shape") || "").toString();
			if (!shape) return;

			this.photoEditor.setCropShapeType(shape);
			jQuery(".mc-photo-shape-btn").removeClass("is-active");
			$btn.addClass("is-active");
		});

		// Cancel/close photo editor
		jQuery(".mc-photo-cancel, .mc-photo-close").on("click", () => {
			this.photoEditor.closePhotoEditor();
		});

		// Apply photo crop
		jQuery(".mc-photo-apply").on("click", () => {
			this.photoEditor.applyPhotoCrop();
		});
	}

	/**
	 * Decor-related event handlers
	 */
	initDecorHandlers() {
		// Filter click
		jQuery(document).on("click", ".mc-decor-filter", (e) => {
			const slug = jQuery(e.target).data("cat") || "";
			jQuery(".mc-decor-filter").removeClass("is-active");
			jQuery(e.target).addClass("is-active");
			this.decorManager.loadDecors(slug);
		});

		// Decor item click
		jQuery(document).on("click", ".mc-decor-item", (e) => {
			const $item = jQuery(e.currentTarget);
			const src = $item.data("image");
			const title = $item.data("title") || "";
			if (!src) return;
			this.decorManager.addDecorImageToCanvas(src, title);
		});
	}

	/**
	 * Candle/vase tab handlers
	 */
	initCandleHandlers() {
		jQuery(".mc-candles-list").on("click", ".mc-candles-list-item", (e) => {
			const $item = jQuery(e.currentTarget);
			const productId = parseInt($item.data("product-id"), 10);
			if (!productId) return;

			this.monumentManager.highlightSelectedCandle(productId);
			this.monumentManager.addCandleToCanvas(productId);
		});
	}

	/**
	 * Monument/product selection handlers
	 */
	initMonumentHandlers() {
		jQuery(".mc-monuments-list").on(
			"click",
			".mc-monuments-list-item",
			(e) => {
				const id = jQuery(e.currentTarget).data("product-id");
				this.appState.currentProductId = parseInt(id, 10);
				this.appState.customMonumentTitle = null;
				this.monumentManager.loadMonumentImage(
					this.appState.currentProductId,
				);
				this.monumentManager.highlightSelectedProduct(
					this.appState.currentProductId,
				);
			},
		);

		const $customButton = jQuery(".mc-add-custom-monument");
		const $customInput = jQuery(".mc-custom-monument-input");

		if ($customButton.length && $customInput.length) {
			$customButton.on("click", () => {
				$customInput.trigger("click");
			});

			$customInput.on("change", (event) => {
				const { files } = event.target;
				if (!files || !files[0]) {
					return;
				}

				const reader = new FileReader();
				reader.onload = (loadEvent) => {
					const result =
						loadEvent && loadEvent.target
							? loadEvent.target.result
							: null;
					if (!result) return;

					this.appState.currentProductId = null;
					this.appState.customMonumentTitle =
						window.MC_DATA.i18n.ownPhoto;

					this.monumentManager
						.loadCustomMonumentImage(result)
						.then(() => {
							this.monumentManager.highlightSelectedProduct(null);
						});
				};

				reader.readAsDataURL(files[0]);
				jQuery(event.target).val("");
			});
		}
	}

	/**
	 * Element toolbar handlers (duplicate, reset, flip, delete)
	 */
	initElementToolbarHandlers() {
		const $imageToolbar = jQuery(".mc-element-toolbar[data-mode='image']");
		const $colorToggleBtn = $imageToolbar.find(".mc-el-color");
		const $groupButtons = jQuery(".mc-el-group");
		const $ungroupButtons = jQuery(".mc-el-ungroup");
		const moveNodeInStack = (node, direction) => {
			if (!node || typeof node[direction] !== "function") return;
			node[direction]();
			const layer = node.getLayer();
			if (layer) {
				layer.batchDraw();
			}
		};

		const moveActiveElement = (direction) => {
			moveNodeInStack(this.appState.activeElementNode, direction);
		};

		const moveActiveText = (direction) => {
			moveNodeInStack(this.appState.activeTextNode, direction);
		};

		// Edit text (open config panel)
		jQuery(".mc-el-edit").on("click", () => {
			if (!this.appState.activeTextNode) return;
			this.textManager.openTextConfig();
		});

		// Duplicate text
		jQuery(".mc-text-toolbar .mc-el-duplicate").on("click", () => {
			if (!this.appState.activeTextNode) return;
			this.textManager.duplicateActiveText();
		});

		jQuery(".mc-text-toolbar .mc-el-move-top").on("click", () => {
			moveActiveText("moveUp");
		});

		jQuery(".mc-text-toolbar .mc-el-move-bottom").on("click", () => {
			moveActiveText("moveDown");
		});

		// Delete text
		jQuery(".mc-text-toolbar .mc-el-delete").on("click", () => {
			if (!this.appState.activeTextNode) return;
			this.textManager.deleteActiveText();
			this.textManager.closeTextConfig();
		});

		// Duplicate element (image/decor)
		jQuery(".mc-element-toolbar[data-mode='image'] .mc-el-duplicate").on(
			"click",
			() => {
				if (!this.appState.activeElementNode) return;

				const mainLayer = this.stageManager.getMainLayer();
				const transformer = this.stageManager.getTransformer();

				const clone = this.appState.activeElementNode.clone({
					x: this.appState.activeElementNode.x() + 10,
					y: this.appState.activeElementNode.y() + 10,
				});

				mainLayer.add(clone);

				// Copy attributes
				clone.setAttr(
					"initialWidth",
					clone.getAttr("initialWidth") || 1,
				);
				clone.setAttr(
					"initialHeight",
					clone.getAttr("initialHeight") || 1,
				);
				clone.setAttr("baseScaleX", clone.getAttr("baseScaleX") || 1);
				clone.setAttr("baseScaleY", clone.getAttr("baseScaleY") || 1);
				clone.setAttr(
					"baseRotation",
					clone.getAttr("baseRotation") || 0,
				);
				clone.setAttr("baseX", clone.getAttr("baseX") ?? clone.x());
				clone.setAttr("baseY", clone.getAttr("baseY") ?? clone.y());

				const selectClone = () => {
					this.appState.setActiveElement(clone);
					if (window.mcSetActiveElement) {
						window.mcSetActiveElement(clone);
					}
					transformer.nodes([clone]);
					transformer.keepRatio(true);
					mainLayer.draw();
				};

				const handleCloneDragStart = () => {
					jQuery(".mc-element-toolbar[data-mode='image']").hide();
				};

				selectClone();

				clone.on("click tap", selectClone);
				clone.on("dragstart", handleCloneDragStart);
				clone.on("dragend", selectClone);
			},
		);

		jQuery(".mc-element-toolbar[data-mode='image'] .mc-el-move-top").on(
			"click",
			() => moveActiveElement("moveUp"),
		);

		jQuery(".mc-element-toolbar[data-mode='image'] .mc-el-move-bottom").on(
			"click",
			() => moveActiveElement("moveDown"),
		);

		// Reset element
		jQuery(".mc-el-reset").on("click", () => {
			if (!this.appState.activeElementNode) return;

			const node = this.appState.activeElementNode;
			const baseScaleX = node.getAttr("baseScaleX") || 1;
			const baseScaleY = node.getAttr("baseScaleY") || 1;
			const baseRotation = node.getAttr("baseRotation") || 0;
			const baseX = node.getAttr("baseX");
			const baseY = node.getAttr("baseY");

			node.scaleX(baseScaleX);
			node.scaleY(baseScaleY);
			node.rotation(baseRotation);
			if (typeof baseX === "number" && typeof baseY === "number") {
				node.position({ x: baseX, y: baseY });
			}

			const mainLayer = this.stageManager.getMainLayer();
			mainLayer.batchDraw();
		});

		// Flip horizontal
		jQuery(".mc-el-flip-h").on("click", () => {
			if (!this.appState.activeElementNode) return;
			const node = this.appState.activeElementNode;
			node.scaleX(node.scaleX() * -1);
			const mainLayer = this.stageManager.getMainLayer();
			mainLayer.batchDraw();
		});

		// Flip vertical
		jQuery(".mc-el-flip-v").on("click", () => {
			if (!this.appState.activeElementNode) return;
			const node = this.appState.activeElementNode;
			node.scaleY(node.scaleY() * -1);
			const mainLayer = this.stageManager.getMainLayer();
			mainLayer.batchDraw();
		});

		// Delete element (image/decor)
		jQuery(".mc-element-toolbar[data-mode='image'] .mc-el-delete").on(
			"click",
			() => {
				this.deleteImageNode();
			},
		);

		$colorToggleBtn.on("click", () => {
			const node = this.appState.activeElementNode;
			if (!node) return;
			const nextState = this.photoEditor.togglePhotoColorMode(node);
			if (nextState === null) return;
			$colorToggleBtn.toggleClass("is-active", !nextState);
		});

		$groupButtons.on("click", (event) => {
			const $btn = jQuery(event.currentTarget);
			if ($btn.prop("disabled")) return;
			this.groupSelectedNodes();
		});

		$ungroupButtons.on("click", (event) => {
			const $btn = jQuery(event.currentTarget);
			if ($btn.prop("disabled")) return;
			this.ungroupSelectedNode();
		});
	}

	/**
	 * Layers panel handlers
	 */
	initLayersPanel() {
		const $panel = jQuery(".mc-layers-panel");
		const $trigger = jQuery(".mc-layers-trigger");
		if (!$panel.length || !$trigger.length) return;

		this.$layersPanel = $panel;
		this.$layersList = $panel.find(".mc-layers-list");
		this.$layersTrigger = $trigger;

		const $close = $panel.find(".mc-layers-close");
		$trigger.on("click", () =>
			this.toggleLayersPanel(!this.isLayersPanelOpen),
		);
		$close.on("click", () => this.toggleLayersPanel(false));

		$panel.on("click", ".mc-layer-row", (event) => {
			event.preventDefault();
			event.stopPropagation();
			const nodeId = jQuery(event.currentTarget).data("nodeId");
			this.handleLayerRowClick(nodeId, event);
		});

		jQuery(document).on("keydown.mcLayersPanel", (evt) => {
			if (!this.isLayersPanelOpen) return;
			if (evt.key === "Escape" || evt.key === "Esc") {
				evt.preventDefault();
				this.toggleLayersPanel(false);
			}
		});

		const outsideClickHandler = (evt) => {
			if (!this.isLayersPanelOpen) return;
			const $target = jQuery(evt.target);
			const clickedInsidePanel =
				$target.closest(".mc-layers-panel").length;
			const clickedTrigger = $target.closest(".mc-layers-trigger").length;
			const clickedCanvas = $target.closest(".konvajs-content").length;
			if (clickedInsidePanel || clickedTrigger || clickedCanvas) {
				return;
			}
			this.toggleLayersPanel(false);
		};

		jQuery(document).on(
			"mousedown.mcLayersPanelOutside touchstart.mcLayersPanelOutside",
			outsideClickHandler,
		);
		this.layersOutsideClickHandler = outsideClickHandler;

		this.registerLayerMutationListeners();
	}

	toggleLayersPanel(forceOpen) {
		if (!this.$layersPanel || !this.$layersPanel.length) return;
		const nextState =
			typeof forceOpen === "boolean"
				? forceOpen
				: !this.isLayersPanelOpen;
		if (nextState === this.isLayersPanelOpen) {
			return;
		}

		if (nextState) {
			this.renderLayersList();
		}

		this.$layersPanel
			.attr("aria-hidden", nextState ? "false" : "true")
			.toggleClass("is-visible", nextState);
		this.$layersTrigger
			.attr("aria-expanded", nextState ? "true" : "false")
			.toggleClass("is-active", nextState);
		this.isLayersPanelOpen = nextState;
	}

	renderLayersList() {
		if (!this.$layersList || !this.$layersList.length) return;
		this.layersNodeIndex = new Map();
		this.$layersList.empty();
		const topNodes = this.getTopLevelLayerNodes();
		if (!topNodes.length) {
			this.$layersList.append(
				'<div class="mc-layers-empty">Nėra sluoksnių</div>',
			);
			return;
		}

		topNodes.forEach((node) => {
			const $row = this.createLayerRow(node, 0);
			if ($row) {
				this.$layersList.append($row);
			}
		});
		this.updateLayersSelectionHighlight();
	}

	createLayerRow(node, depth = 0) {
		if (!node) return null;
		const nodeId = String(node._id);
		this.layersNodeIndex.set(nodeId, node);
		const $row = jQuery("<div class='mc-layer-row' role='treeitem'></div>")
			.attr("data-node-id", nodeId)
			.attr("data-depth", depth)
			.attr("aria-level", depth + 1);

		const $header = jQuery("<div class='mc-layer-row__header'></div>");
		const $type = jQuery("<span class='mc-layer-row__type'></span>").text(
			this.getLayerTypeLabel(node),
		);
		const $label = jQuery("<span class='mc-layer-row__label'></span>").text(
			this.getLayerDisplayName(node),
		);
		$header.append($type, $label);
		$row.append($header);

		if (this.isGroupNode(node)) {
			$row.addClass("mc-layer-row--group");
			const children = this.getGroupChildren(node);
			if (children.length) {
				const $children = jQuery(
					"<div class='mc-layer-children' role='group'></div>",
				);
				children.forEach((child) => {
					const $childRow = this.createLayerRow(child, depth + 1);
					if ($childRow) {
						$children.append($childRow);
					}
				});
				$row.append($children);
			}
		}

		return $row;
	}

	getTopLevelLayerNodes() {
		const mainLayer = this.stageManager.getMainLayer();
		if (!mainLayer || typeof mainLayer.getChildren !== "function") {
			return [];
		}
		const childrenCollection = mainLayer.getChildren();
		const children = this.toNodeArray(childrenCollection);
		return children
			.filter((node) => this.isGroupableNode(node))
			.sort((a, b) => this.getNodeZIndex(b) - this.getNodeZIndex(a));
	}

	getGroupChildren(group) {
		if (!group || typeof group.getChildren !== "function") return [];
		return this.toNodeArray(group.getChildren())
			.filter((node) => this.isGroupableNode(node))
			.sort((a, b) => this.getNodeZIndex(b) - this.getNodeZIndex(a));
	}

	getLayerDisplayName(node) {
		if (this.isTextNode(node)) {
			const value =
				typeof node.text === "function" ? node.text() : node.text || "";
			const text = value ? String(value) : window.MC_DATA.i18n.text;
			return text.slice(0, 40);
		}
		if (this.isPhotoNode(node)) {
			return window.MC_DATA.i18n.photo;
		}
		if (this.isGroupNode(node)) {
			return window.MC_DATA.i18n.group;
		}
		if (this.isDecorNode(node)) {
			return node.getAttr("decorTitle") || window.MC_DATA.i18n.decor;
		}
		return window.MC_DATA.i18n.element;
	}

	getLayerTypeLabel(node) {
		if (this.isGroupNode(node)) return window.MC_DATA.i18n.group;
		if (this.isTextNode(node)) return window.MC_DATA.i18n.text;
		if (this.isPhotoNode(node)) return window.MC_DATA.i18n.photo;
		if (this.isDecorNode(node)) {
			const type = node.getAttr("decorType") || window.MC_DATA.i18n.decor;
			return type === "candle"
				? window.MC_DATA.i18n.candle
				: window.MC_DATA.i18n.decor;
		}
		return window.MC_DATA.i18n.element;
	}

	handleLayerRowClick(nodeId, event) {
		if (!nodeId) return;
		const node = this.layersNodeIndex.get(String(nodeId));
		if (!node) return;
		const nativeEvent =
			event && event.originalEvent ? event.originalEvent : event;
		const multiSelect = this.shouldMultiSelect({
			originalEvent: nativeEvent,
		});
		if (this.isTextNode(node)) {
			this.setActiveTextElement(node, {
				multiSelect,
				autoOpenConfig: false,
				originalEvent: nativeEvent,
			});
		} else {
			this.setActiveElement(node, {
				multiSelect,
				originalEvent: nativeEvent,
			});
		}
		this.updateLayersSelectionHighlight();
		if (this.isLayersPanelOpen) {
			this.toggleLayersPanel(false);
		}
	}

	updateLayersSelectionHighlight() {
		if (!this.$layersList || !this.$layersList.length) return;
		const selected = this.getSelectedNodes();
		const selectedIds = new Set(
			selected.map((node) => String(node && node._id)).filter(Boolean),
		);
		this.$layersList.find(".mc-layer-row").each((_, el) => {
			const $row = jQuery(el);
			const nodeId = String($row.data("nodeId"));
			$row.toggleClass("is-selected", selectedIds.has(nodeId));
		});
	}

	refreshLayersListIfVisible() {
		if (this.isLayersPanelOpen) {
			this.renderLayersList();
		}
	}

	registerLayerMutationListeners() {
		const mainLayer = this.stageManager.getMainLayer();
		if (!mainLayer || typeof mainLayer.on !== "function") return;
		const refreshIfRelevant = (evt) => {
			const target = evt && evt.target ? evt.target : null;
			if (this.isGroupableNode(target)) {
				this.refreshLayersListIfVisible();
			}
		};
		mainLayer.on("add", refreshIfRelevant);
		mainLayer.on("remove", refreshIfRelevant);
	}

	toNodeArray(collection) {
		if (!collection) return [];
		if (Array.isArray(collection)) {
			return collection.slice();
		}
		if (typeof collection.toArray === "function") {
			return collection.toArray();
		}
		const nodes = [];
		if (typeof collection.each === "function") {
			collection.each((node) => nodes.push(node));
		}
		return nodes;
	}

	/**
	 * Stage interaction handlers
	 */
	initStageHandlers() {
		const stage = this.stageManager.getStage();

		stage.on("mousedown touchstart", (e) => {
			if (e.target === stage && this.isLayersPanelOpen) {
				this.toggleLayersPanel(false);
			}

			if (e.target !== stage) {
				this.cancelDragSelection();
				return;
			}

			const pointer = stage.getPointerPosition();
			if (!pointer) return;
			this.stageManager.setPhotosNotActive();
			this.beginDragSelection(pointer);
		});

		stage.on("mousemove touchmove", () => {
			if (!this.selectionDragActive) return;
			const pointer = stage.getPointerPosition();
			if (!pointer) return;
			this.updateDragSelection(pointer);
		});

		stage.on("mouseup touchend", (e) => {
			if (!this.selectionDragActive) {
				if (e.target === stage) {
					this.clearSelection();
					this.stageManager.setPhotosNotActive();
					this.stageManager.getMainLayer().draw();
					return;
				}
			}
			this.completeDragSelection(e);
		});

		stage.on("mouseleave touchcancel", () => {
			this.cancelDragSelection();
		});
	}

	beginDragSelection(pointer) {
		this.selectionDragActive = true;
		this.selectionDragOrigin = { x: pointer.x, y: pointer.y };
		const rect = this.ensureSelectionRect();
		rect.visible(false);
		rect.width(0);
		rect.height(0);
		rect.position({ x: pointer.x, y: pointer.y });
		this.stageManager.getMainLayer().draw();
	}

	updateDragSelection(pointer) {
		if (!this.selectionDragActive) return;
		const origin = this.selectionDragOrigin;
		if (!origin) return;

		const rect = this.ensureSelectionRect();
		const width = pointer.x - origin.x;
		const height = pointer.y - origin.y;
		const nextX = width >= 0 ? origin.x : origin.x + width;
		const nextY = height >= 0 ? origin.y : origin.y + height;
		const absWidth = Math.abs(width);
		const absHeight = Math.abs(height);
		const minSize = 4;
		const shouldShow = absWidth >= minSize && absHeight >= minSize;

		rect.position({ x: nextX, y: nextY });
		rect.size({ width: absWidth, height: absHeight });
		rect.visible(shouldShow);
		this.stageManager.getMainLayer().draw();
	}

	completeDragSelection(evt) {
		if (!this.selectionDragActive) return;
		this.selectionDragActive = false;
		const rect = this.ensureSelectionRect();
		const mainLayer = this.stageManager.getMainLayer();
		const stage = this.stageManager.getStage();
		const wasVisible = rect.visible();
		const selectionBounds = wasVisible
			? rect.getClientRect({ relativeTo: stage })
			: null;
		rect.visible(false);
		rect.width(0);
		rect.height(0);
		mainLayer.draw();

		if (wasVisible) {
			const nodes = this.getNodesWithinSelectionRect(selectionBounds);
			this.applySelection(nodes);
		} else if (evt && evt.target === this.stageManager.getStage()) {
			this.clearSelection();
			this.stageManager.setPhotosNotActive();
			mainLayer.draw();
		}

		this.selectionDragOrigin = null;
	}

	cancelDragSelection() {
		if (!this.selectionDragActive) return;
		this.selectionDragActive = false;
		const rect = this.ensureSelectionRect();
		rect.visible(false);
		rect.width(0);
		rect.height(0);
		this.selectionDragOrigin = null;
		this.stageManager.getMainLayer().draw();
	}

	ensureSelectionRect() {
		if (this.selectionRect) return this.selectionRect;
		const rect = new Konva.Rect({
			fill: "rgba(32, 137, 247, 0.15)",
			stroke: "#2089f7",
			strokeWidth: 1,
			dash: [4, 4],
			visible: false,
			listening: false,
		});
		this.selectionRect = rect;
		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.add(rect);
		const transformer = this.stageManager.getTransformer();
		if (transformer) {
			transformer.moveToTop();
		}
		mainLayer.draw();
		return rect;
	}

	getNodesWithinSelectionRect(selectionBounds = null) {
		const stage = this.stageManager.getStage();
		const bounds =
			selectionBounds ||
			(this.selectionRect
				? this.selectionRect.getClientRect({ relativeTo: stage })
				: null);
		if (!bounds) return [];

		const selectors = [
			".mc-text",
			".mc-decor",
			".mc-photo-group",
			".mc-group",
		];
		const results = [];
		selectors.forEach((selector) => {
			const collection = stage.find(selector) || [];
			collection.forEach((node) => {
				if (!this.isGroupableNode(node)) return;
				const nodeBounds = node.getClientRect({ relativeTo: stage });
				if (!nodeBounds) return;
				if (this.rectsIntersect(bounds, nodeBounds)) {
					results.push(node);
				}
			});
		});

		return results;
	}

	rectsIntersect(a, b) {
		if (!a || !b) return false;
		const aRight = a.x + a.width;
		const aBottom = a.y + a.height;
		const bRight = b.x + b.width;
		const bBottom = b.y + b.height;
		return !(
			b.x > aRight ||
			bRight < a.x ||
			b.y > aBottom ||
			bBottom < a.y
		);
	}

	getNodeZIndex(node) {
		if (!node || typeof node.getZIndex !== "function") return 0;
		const value = node.getZIndex();
		return typeof value === "number" ? value : 0;
	}

	/**
	 * Keyboard event handlers
	 */
	initKeyboardHandlers() {
		const arrowKeyMap = {
			ArrowUp: { dx: 0, dy: -1 },
			ArrowDown: { dx: 0, dy: 1 },
			ArrowLeft: { dx: -1, dy: 0 },
			ArrowRight: { dx: 1, dy: 0 },
		};

		jQuery(document).on("keydown", (e) => {
			const tag = (e.target.tagName || "").toLowerCase();
			const isTyping =
				tag === "input" ||
				tag === "textarea" ||
				e.target.isContentEditable;
			const selection = this.getSelectedNodes();

			if (!isTyping && arrowKeyMap[e.key]) {
				if (selection.length) {
					const { dx, dy } = arrowKeyMap[e.key];
					e.preventDefault();
					this.nudgeNodes(selection, dx, dy);
				}
				return;
			}

			// ESC key
			if (e.key === "Escape" || e.key === "Esc") {
				if (selection.length) {
					e.preventDefault();
					this.clearSelection();
					return;
				}
			}

			// DELETE/BACKSPACE key
			if ((e.key === "Delete" || e.key === "Backspace") && !isTyping) {
				if (selection.length) {
					e.preventDefault();
					this.deleteImageNode();
					return;
				}

				if (this.appState.activeTextNode) {
					e.preventDefault();
					this.textManager.deleteActiveText();
					this.textManager.closeTextConfig();
					return;
				}
			}
		});
	}

	/**
	 * Contact Form 7 submission handler
	 */
	initFormHandlers() {
		const attachInterceptors = () => {
			const forms = document.querySelectorAll(".wpcf7 form");
			forms.forEach((form) => {
				if (!this.isTargetCf7Form(form)) return;
				if (form.dataset.mcCf7Intercept === "1") return;

				form.dataset.mcCf7Intercept = "1";
				this.ensureRequestIdField(form);
				form.addEventListener(
					"submit",
					(event) => this.handleCf7Submit(event),
					true,
				);
			});
		};

		attachInterceptors();

		const resetHandlers = () => {
			attachInterceptors();
		};

		document.addEventListener("wpcf7submit", (event) => {
			if (!this.isTargetCf7Form(event.target)) return;
			this.setFormLoadingState(event.target, false);
			this.resetRequestIdField(event.target);
			this.clearNativeSubmitFlag(event.target);
			delete event.target.dataset.mcSaving;
			setTimeout(resetHandlers, 0);
		});

		["wpcf7mailfailed", "wpcf7invalid", "wpcf7spam"].forEach(
			(eventName) => {
				document.addEventListener(eventName, (event) => {
					if (!this.isTargetCf7Form(event.target)) return;
					this.setFormLoadingState(event.target, false);
					this.clearNativeSubmitFlag(event.target);
					delete event.target.dataset.mcSaving;
				});
			},
		);
	}

	handleCf7Submit(event) {
		const form = event.target;

		if (!this.isTargetCf7Form(form)) return;

		if (form.dataset.mcAllowNativeSubmit === "1") {
			delete form.dataset.mcAllowNativeSubmit;
			return;
		}

		if (form.dataset.mcSaving === "1") {
			event.preventDefault();
			return;
		}

		event.preventDefault();
		event.stopImmediatePropagation();
		form.dataset.mcSaving = "1";
		this.setFormLoadingState(form, true);

		this.clearSelection();

		const constructorState = this.stageManager.serializeState(
			this.appState.currentProductId,
			this.appState.customMonumentTitle,
		);
		const previewPNG = this.stageManager.getPreviewPNG();
		const cf7Fields = this.collectCf7Fields(form);

		jQuery
			.ajax({
				url: window.MC_DATA.ajax_url,
				type: "POST",
				dataType: "json",
				data: {
					action: "mc_create_constructor",
					nonce: window.MC_DATA.ajax_nonce,
					mc_token: window.MC_DATA.mc_token,
					mc_token_time: window.MC_DATA.mc_token_time,
					mc_website_url: "",
					constructor_state: JSON.stringify(constructorState),
					preview_image: previewPNG,
					cf7_fields: cf7Fields,
				},
			})
			.done((response) => {
				const isSuccess =
					response &&
					response.success &&
					response.data &&
					response.data.post_id;

				if (!isSuccess) {
					console.error("Constructor save failed - no post ID");
					this.handleConstructorSaveError(
						form,
						"Missing constructor ID",
					);
					return;
				}

				this.ensureRequestIdField(form).value = response.data.post_id;
				form.dataset.mcAllowNativeSubmit = "1";
				delete form.dataset.mcSaving;

				if (typeof form.requestSubmit === "function") {
					form.requestSubmit();
				} else {
					const submitEvent = document.createEvent("Event");
					submitEvent.initEvent("submit", true, true);
					form.dispatchEvent(submitEvent);
				}
			})
			.fail((xhr, status, error) => {
				console.error("XHR:", xhr);
				console.error("Status:", status);
				console.error("Error:", error);
				console.error("Response Text:", xhr.responseText);

				this.handleConstructorSaveError(
					form,
					error || status || "error",
				);
			});
	}

	handleConstructorSaveError(form, logMessage) {
		delete form.dataset.mcSaving;
		this.setFormLoadingState(form, false);
		if (logMessage) {
			console.error("MC constructor save error:", logMessage);
		}
		const fallbackMessage =
			(window.MC_DATA && window.MC_DATA.requestErrorMessage) ||
			"Įvyko klaida išsaugant užklausą. Bandykite dar kartą.";
		window.alert(fallbackMessage);
	}

	isTargetCf7Form(form) {
		if (
			!form ||
			!form.classList ||
			!form.classList.contains("wpcf7-form")
		) {
			return false;
		}

		const idField = form.querySelector('input[name="_wpcf7"]');
		if (!idField) return false;
		const formId = parseInt(idField.value, 10);
		return formId === this.getTargetCf7FormId();
	}

	getTargetCf7FormId() {
		const runtimeId =
			window.MC_DATA && window.MC_DATA.cf7_form_id
				? parseInt(window.MC_DATA.cf7_form_id, 10)
				: NaN;
		return Number.isFinite(runtimeId) ? runtimeId : CF7_FORM_ID;
	}

	ensureRequestIdField(form) {
		let field = form.querySelector('input[name="mc-request-id"]');
		if (!field) {
			field = document.createElement("input");
			field.type = "hidden";
			field.name = "mc-request-id";
			form.appendChild(field);
		}
		return field;
	}

	resetRequestIdField(form) {
		const field = form.querySelector('input[name="mc-request-id"]');
		if (field) {
			field.value = "";
		}
	}

	clearNativeSubmitFlag(form) {
		if (form && form.dataset) {
			delete form.dataset.mcAllowNativeSubmit;
		}
	}

	setFormLoadingState(form, isLoading) {
		const $form = jQuery(form);
		const $submitButtons = $form.find(
			'button[type="submit"], input[type="submit"]',
		);

		$form.toggleClass("mc-cf7-saving", !!isLoading);
		$submitButtons.prop("disabled", !!isLoading);

		if (isLoading) {
			let spinner = form.querySelector(".mc-cf7-spinner");
			if (!spinner) {
				spinner = document.createElement("div");
				spinner.className = "mc-cf7-spinner";
				spinner.setAttribute("aria-live", "polite");
				spinner.innerHTML =
					'<span class="mc-cf7-spinner__loader" aria-hidden="true"></span>';
				form.appendChild(spinner);
			}
		} else {
			const spinner = form.querySelector(".mc-cf7-spinner");
			if (spinner) {
				spinner.remove();
			}
		}
	}

	collectCf7Fields(form) {
		const formData = new window.FormData(form);
		const cf7Fields = {};

		formData.forEach((value, key) => {
			if (value instanceof window.File) {
				return;
			}

			if (cf7Fields[key]) {
				if (Array.isArray(cf7Fields[key])) {
					cf7Fields[key].push(value);
				} else {
					cf7Fields[key] = [cf7Fields[key], value];
				}
			} else {
				cf7Fields[key] = value;
			}
		});

		return cf7Fields;
	}

	/**
	 * Delete currently selected nodes (decor, photo, groups)
	 */
	deleteImageNode() {
		const selectedNodes = this.getSelectedNodes();
		const targets = selectedNodes.length
			? selectedNodes
			: this.appState.activeElementNode
				? [this.appState.activeElementNode]
				: [];

		if (!targets.length) return;

		targets.forEach((node) => {
			if (node && typeof node.destroy === "function") {
				node.destroy();
			}
		});

		this.clearSelection();
		this.stageManager.getMainLayer().draw();
		this.refreshLayersListIfVisible();
	}

	setActiveElement(node, options = {}) {
		const selection = this.buildNextSelection(node, options);
		this.applySelection(selection, {});
	}

	setActiveTextElement(node, options = {}) {
		const selection = this.buildNextSelection(node, options);
		this.applySelection(selection, {
			autoOpenTextConfig: !!options.autoOpenConfig,
		});
	}

	clearSelection() {
		this.applySelection([]);
	}

	getSelectedNodes() {
		return this.appState.getSelectedNodes();
	}

	setSelectedNodes(nodes) {
		this.appState.setSelectedNodes(nodes);
	}

	updateSelectionToolbarPosition() {
		const nodes = this.getSelectedNodes();
		if (!nodes.length) return;

		const $textToolbar = jQuery(".mc-text-toolbar");
		const $imageToolbar = jQuery(".mc-element-toolbar[data-mode='image']");

		if (nodes.length > 1) {
			const rect = this.getSelectionBoundingRect(nodes);
			if (rect) {
				this.positionToolbar(rect, $imageToolbar);
			}
			return;
		}

		const node = nodes[0];
		if (this.isTextNode(node)) {
			this.positionToolbar(node, $textToolbar);
			return;
		}

		this.positionToolbar(node, $imageToolbar);
	}

	nudgeNodes(nodes, dx = 0, dy = 0) {
		if (!Array.isArray(nodes) || !nodes.length) return;
		if (!dx && !dy) return;

		let moved = false;
		nodes.forEach((node) => {
			if (
				!node ||
				typeof node.x !== "function" ||
				typeof node.y !== "function"
			) {
				return;
			}
			node.x(node.x() + dx);
			node.y(node.y() + dy);
			moved = true;
		});

		if (!moved) return;

		const mainLayer = this.stageManager.getMainLayer();
		if (mainLayer && typeof mainLayer.batchDraw === "function") {
			mainLayer.batchDraw();
		}

		this.updateSelectionToolbarPosition();
		this.refreshLayersListIfVisible();
	}

	shouldMultiSelect(options = {}) {
		if (typeof options.multiSelect === "boolean") {
			return options.multiSelect;
		}
		const nativeEvent = options.originalEvent || options.event;
		if (!nativeEvent) return false;
		return Boolean(
			nativeEvent.shiftKey || nativeEvent.metaKey || nativeEvent.ctrlKey,
		);
	}

	buildNextSelection(node, options = {}) {
		const current = this.getSelectedNodes();
		const multiSelect = this.shouldMultiSelect(options);
		if (multiSelect && node) {
			const exists = current.includes(node);
			return exists
				? current.filter((item) => item !== node)
				: [...current, node];
		}
		if (!node) {
			return multiSelect ? current : [];
		}
		return [node];
	}

	applySelection(nodes, options = {}) {
		const uniqueNodes = [];
		nodes.forEach((node) => {
			if (node && !uniqueNodes.includes(node)) {
				uniqueNodes.push(node);
			}
		});

		this.setSelectedNodes(uniqueNodes);

		const transformer = this.stageManager.getTransformer();
		const mainLayer = this.stageManager.getMainLayer();
		const $textToolbar = jQuery(".mc-text-toolbar");
		const $imageToolbar = jQuery(".mc-element-toolbar[data-mode='image']");
		const $colorToggleBtn = $imageToolbar.find(".mc-el-color");

		if (!uniqueNodes.length) {
			this.appState.clearActiveElements();
			this.textManager.closeTextConfig();
			transformer.nodes([]);
			$textToolbar.hide();
			$imageToolbar.hide();
			$colorToggleBtn
				.prop("disabled", true)
				.removeClass("is-active")
				.addClass("is-hidden");
			this.updateGroupButtonState();
			this.updateLayersSelectionHighlight();
			return;
		}

		if (uniqueNodes.length > 1) {
			this.appState.setActiveText(null);
			this.appState.setActiveDecor(null);
			this.appState.setActiveElement(null);
			this.appState.setActiveGroup(null);
			this.textManager.closeTextConfig();

			this.stageManager.setPhotosNotActive();
			transformer.nodes(uniqueNodes);
			transformer.rotateEnabled(true);
			transformer.keepRatio(false);
			transformer.enabledAnchors([
				"top-left",
				"top-center",
				"top-right",
				"middle-right",
				"bottom-right",
				"bottom-center",
				"bottom-left",
				"middle-left",
			]);
			mainLayer.draw();

			$textToolbar.hide();
			$imageToolbar.css("display", "flex");
			const rect = this.getSelectionBoundingRect(uniqueNodes);
			if (rect) {
				this.positionToolbar(rect, $imageToolbar);
			}
			$colorToggleBtn
				.prop("disabled", true)
				.removeClass("is-active")
				.addClass("is-hidden");
			this.updateGroupButtonState();
			this.updateLayersSelectionHighlight();
			return;
		}

		const node = uniqueNodes[0];
		const isText = this.isTextNode(node);
		const isPhoto = this.isPhotoNode(node);
		const isGroup = this.isGroupNode(node);

		if (isText) {
			this.appState.setActiveDecor(null);
			this.appState.setActiveElement(null);
			this.appState.setActiveGroup(null);
			this.textManager.setActiveText(node);
			$imageToolbar.hide();
			$textToolbar.css("display", "flex");
			this.positionToolbar(node, $textToolbar);
			if (options.autoOpenTextConfig) {
				this.textManager.openTextConfig();
			}
		} else {
			this.textManager.setActiveText(null);
			this.textManager.closeTextConfig();
			this.appState.setActiveElement(node);
			this.appState.setActiveDecor(this.isDecorNode(node) ? node : null);
			this.appState.setActiveGroup(isGroup ? node : null);

			$textToolbar.hide();
			$imageToolbar.css("display", "flex");
			this.positionToolbar(node, $imageToolbar);

			if (!isPhoto) {
				this.stageManager.setPhotosNotActive();
				transformer.nodes([node]);
				transformer.rotateEnabled(true);
				transformer.keepRatio(!isGroup);
				transformer.enabledAnchors([
					"top-left",
					"top-center",
					"top-right",
					"middle-right",
					"bottom-right",
					"bottom-center",
					"bottom-left",
					"middle-left",
				]);
				mainLayer.draw();
			} else {
				transformer.nodes([]);
			}

			if (isPhoto) {
				$colorToggleBtn
					.prop("disabled", false)
					.removeClass("is-hidden");
				const storedState = node.getAttr("mcIsGrayscale");
				const isGrayscale =
					typeof storedState === "boolean" ? storedState : true;
				$colorToggleBtn.toggleClass("is-active", !isGrayscale);
			} else {
				$colorToggleBtn
					.prop("disabled", true)
					.removeClass("is-active")
					.addClass("is-hidden");
			}
		}

		this.updateGroupButtonState();
		this.updateLayersSelectionHighlight();
	}

	getSelectionBoundingRect(nodes) {
		const stage = this.stageManager.getStage();
		const rects = nodes
			.map((node) =>
				typeof node.getClientRect === "function"
					? node.getClientRect({ relativeTo: stage })
					: null,
			)
			.filter(Boolean);
		if (!rects.length) return null;
		const minX = Math.min(...rects.map((rect) => rect.x));
		const minY = Math.min(...rects.map((rect) => rect.y));
		const maxX = Math.max(...rects.map((rect) => rect.x + rect.width));
		const maxY = Math.max(...rects.map((rect) => rect.y + rect.height));
		return {
			x: minX,
			y: minY,
			width: maxX - minX,
			height: maxY - minY,
		};
	}

	isTextNode(node) {
		return (
			node &&
			typeof node.hasName === "function" &&
			node.hasName("mc-text")
		);
	}

	isPhotoNode(node) {
		return (
			node &&
			typeof node.hasName === "function" &&
			node.hasName("mc-photo-group")
		);
	}

	isDecorNode(node) {
		return (
			node &&
			typeof node.hasName === "function" &&
			node.hasName("mc-decor")
		);
	}

	isGroupNode(node) {
		return node && node.getAttr && node.getAttr("mcIsGroup");
	}

	isGroupableNode(node) {
		return (
			this.isTextNode(node) ||
			this.isDecorNode(node) ||
			this.isPhotoNode(node) ||
			this.isGroupNode(node)
		);
	}

	groupSelectedNodes() {
		const nodes = this.getSelectedNodes().filter((node) =>
			this.isGroupableNode(node),
		);
		if (nodes.length < 2) return;

		const boundingRect = this.getSelectionBoundingRect(nodes);
		if (!boundingRect) return;

		const mainLayer = this.stageManager.getMainLayer();
		const group = new Konva.Group({
			x: boundingRect.x,
			y: boundingRect.y,
			draggable: true,
			name: "mc-group",
		});
		group.setAttr("mcIsGroup", true);
		mainLayer.add(group);

		const sortedNodes = [...nodes].sort(
			(a, b) => this.getNodeZIndex(a) - this.getNodeZIndex(b),
		);
		const minZIndex = sortedNodes.reduce((min, node) => {
			const z = this.getNodeZIndex(node);
			return Number.isFinite(z) ? Math.min(min, z) : min;
		}, Number.POSITIVE_INFINITY);

		sortedNodes.forEach((node) => {
			const absPos = node.getAbsolutePosition();
			this.lockNodeForGrouping(node);
			node.moveTo(group);
			node.position({
				x: absPos.x - boundingRect.x,
				y: absPos.y - boundingRect.y,
			});
		});

		if (Number.isFinite(minZIndex) && typeof group.zIndex === "function") {
			group.zIndex(minZIndex);
		}

		this.registerGroupNodeEvents(group);
		mainLayer.draw();
		this.applySelection([group]);
		this.refreshLayersListIfVisible();
	}

	ungroupSelectedNode() {
		const nodes = this.getSelectedNodes();
		if (nodes.length !== 1) return;
		const group = nodes[0];
		if (!this.isGroupNode(group)) return;

		const mainLayer = this.stageManager.getMainLayer();
		const children = group.getChildren().slice();
		const baseZIndex = this.getNodeZIndex(group);
		children.forEach((child) => {
			const absPos = child.getAbsolutePosition();
			child.moveTo(mainLayer);
			child.position({ x: absPos.x, y: absPos.y });
			this.unlockNodeFromGrouping(child);
			this.rebindNodeSelectionHandlers(child);
		});
		if (Number.isFinite(baseZIndex)) {
			children.forEach((child, index) => {
				if (typeof child.zIndex === "function") {
					child.zIndex(baseZIndex + index);
				}
			});
		}

		group.destroy();
		mainLayer.draw();
		this.applySelection(children);
		this.refreshLayersListIfVisible();
	}

	updateGroupButtonState() {
		const $groupButtons = jQuery(".mc-el-group");
		const $ungroupButtons = jQuery(".mc-el-ungroup");
		if (!$groupButtons.length && !$ungroupButtons.length) return;

		const nodes = this.getSelectedNodes();
		const canGroup =
			nodes.length > 1 &&
			nodes.every((node) => this.isGroupableNode(node));
		const canUngroup = nodes.length === 1 && this.isGroupNode(nodes[0]);
		const showGroup = nodes.length > 1;
		const showUngroup = nodes.length === 1 && this.isGroupNode(nodes[0]);

		$groupButtons.prop("disabled", !canGroup);
		$groupButtons.toggleClass("is-hidden", !showGroup);
		$ungroupButtons.prop("disabled", !canUngroup);
		$ungroupButtons.toggleClass("is-hidden", !showUngroup);
	}

	registerGroupNodeEvents(group) {
		if (!group || typeof group.on !== "function") return;
		const handleSelect = (evt) => {
			const nativeEvent = this.extractNativeEvent(evt);
			const multiSelect = this.shouldMultiSelect({
				originalEvent: nativeEvent,
			});
			this.setActiveElement(group, {
				multiSelect,
				originalEvent: nativeEvent,
			});
		};
		const handleDragStart = () => {
			jQuery(".mc-element-toolbar[data-mode='image']").hide();
		};
		group.on("click tap", handleSelect);
		group.on("dragstart", handleDragStart);
		group.on("dragend", handleSelect);
	}

	lockNodeForGrouping(node) {
		if (!node) return;
		if (typeof node.draggable === "function") {
			node.setAttr("mcGroupPrevDraggable", node.draggable());
			node.draggable(false);
		}
	}

	unlockNodeFromGrouping(node) {
		if (!node) return;
		const prevDraggable = node.getAttr("mcGroupPrevDraggable");
		if (typeof node.draggable === "function") {
			node.draggable(
				typeof prevDraggable === "boolean" ? prevDraggable : true,
			);
		}
		node.setAttr("mcGroupPrevDraggable", undefined);
	}

	rebindNodeSelectionHandlers(node) {
		if (!node || typeof node.off !== "function") return;
		node.off("click tap");
		node.off("dragstart");
		node.off("dragend");

		if (this.isGroupNode(node)) {
			this.registerGroupNodeEvents(node);
			return;
		}

		if (this.isTextNode(node)) {
			if (
				this.textManager &&
				typeof this.textManager.bindTextNodeInteractions === "function"
			) {
				this.textManager.bindTextNodeInteractions(node);
			}
			return;
		}

		if (this.isPhotoNode(node)) {
			const transformer = this.stageManager.getTransformer();
			const photoTransformer = this.getOrCreatePhotoTransformer(node);
			const handleSelect = (evt) => {
				const nativeEvent = this.extractNativeEvent(evt);
				const multiSelect = this.shouldMultiSelect({
					originalEvent: nativeEvent,
				});
				if (multiSelect) {
					photoTransformer.nodes([]);
				} else {
					transformer.nodes([]);
					photoTransformer.nodes([node]);
				}
				this.setActiveElement(node, {
					multiSelect,
					originalEvent: nativeEvent,
				});
				const layer = node.getLayer();
				if (layer) {
					layer.draw();
				}
			};
			const handleDragStart = () => {
				jQuery(".mc-element-toolbar[data-mode='image']").hide();
			};
			node.on("click tap", handleSelect);
			node.on("dragstart", handleDragStart);
			node.on("dragend", handleSelect);
			return;
		}

		const handleSelect = (evt) => {
			const nativeEvent = this.extractNativeEvent(evt);
			const multiSelect = this.shouldMultiSelect({
				originalEvent: nativeEvent,
			});
			this.setActiveElement(node, {
				multiSelect,
				originalEvent: nativeEvent,
			});
		};
		const handleDragStart = () => {
			jQuery(".mc-element-toolbar[data-mode='image']").hide();
		};
		node.on("click tap", handleSelect);
		node.on("dragstart", handleDragStart);
		node.on("dragend", handleSelect);
	}

	getOrCreatePhotoTransformer(node) {
		const mainLayer = this.stageManager.getMainLayer();
		const transformerCollection = mainLayer.find("Transformer");
		const transformers = [];
		if (
			transformerCollection &&
			typeof transformerCollection.each === "function"
		) {
			transformerCollection.each((tr) => transformers.push(tr));
		}
		const mainTransformer = this.stageManager.getTransformer();
		const nodeId = node ? node._id : null;
		let photoTransformer = null;
		const storedTransformerId = node
			? node.getAttr("mcPhotoTransformerRef")
			: null;

		if (storedTransformerId) {
			photoTransformer = transformers.find(
				(tr) => tr._id === storedTransformerId,
			);
			if (
				photoTransformer &&
				photoTransformer.getAttr("mcPhotoOwnerId") !== nodeId
			) {
				photoTransformer = null;
			}
		}

		if (!photoTransformer) {
			photoTransformer = transformers.find((tr) => {
				if (tr === mainTransformer) return false;
				return tr.getAttr("mcPhotoOwnerId") === nodeId;
			});
		}

		if (!photoTransformer) {
			photoTransformer = this.createPhotoTransformerForNode(node);
		}

		if (photoTransformer) {
			photoTransformer.setAttr("mcPhotoOwnerId", nodeId);
			node.setAttr("mcPhotoTransformerRef", photoTransformer._id);
		}

		return photoTransformer;
	}

	createPhotoTransformerForNode(node) {
		const mainLayer = this.stageManager.getMainLayer();
		const shapeType = node ? node.getAttr("photoShapeType") : null;
		const lockAspect = shapeType === "oval" || shapeType === "rectangle";
		const transformer = new Konva.Transformer({
			rotateEnabled: true,
			keepRatio: !!lockAspect,
			rotateAnchorOffset: 20,
			enabledAnchors: [
				"top-left",
				"top-right",
				"bottom-right",
				"bottom-left",
			],
			boundBoxFunc: (oldBox, newBox) => {
				if (newBox.width < 40 || newBox.height < 40) {
					return oldBox;
				}
				return newBox;
			},
		});
		mainLayer.add(transformer);
		return transformer;
	}

	extractNativeEvent(evt) {
		return evt && evt.evt ? evt.evt : evt;
	}
}
